/**
 * LensFlareConfig — parameters for the lens-flares post pass.
 *
 * Three composable effects:
 *   - **Ghost flares** (Maughan 2007 GPU Gems 3 ch.11): bright pixels are
 *     mirrored through screen center and tinted; multiple "ghosts" with
 *     scaling + chromatic offsets give the classic lens-flare look.
 *   - **Anamorphic streak**: separable horizontal blur on bright spots —
 *     the cinematic blue horizontal-line look. (Optional — the engine's
 *     existing PostProcessingPipeline `anamorphic` slot is the canonical
 *     home for this; this module's own streak is a fallback.)
 *   - **Star burst**: radial blur from very bright spots to imitate the
 *     star pattern of an iris with N blades.
 *
 * v1 ships:
 *   - 4 ghosts (default)
 *   - Optional starburst (8 spokes default)
 *   - Optional self-contained anamorphic streak
 *   - Sun-aware screen-space gating (uses SkyModule sun direction if present)
 *
 * v2 roadmap:
 *   - Lens dirt mask asset (multiplied over composite for "smudgy lens")
 *   - Per-light flares (LensflareMesh-style, attached to discrete lights)
 *   - Hullin 2011 physically-based lens flares (per-element optical model)
 */

export interface LensFlareGhost {
  /** Mirror-scale around screen center. -1 = exact mirror, 0.5 = halfway. */
  scale: number;
  /** Color tint multiplier. */
  color: { r: number; g: number; b: number };
  /** Chromatic offset (UV pixels) for R/B fringe. Default 2-4 px. */
  chromaticOffset: number;
}

export const DEFAULT_GHOSTS: LensFlareGhost[] = [
  { scale:  0.7, color: { r: 1.0, g: 0.6, b: 0.3 }, chromaticOffset: 2 }, // warm orange-red
  { scale: -0.4, color: { r: 0.4, g: 0.8, b: 1.0 }, chromaticOffset: 3 }, // cool cyan-blue
  { scale:  0.25, color: { r: 1.0, g: 1.0, b: 0.6 }, chromaticOffset: 1 }, // pale yellow
  { scale: -0.15, color: { r: 0.7, g: 0.4, b: 1.0 }, chromaticOffset: 2 }, // magenta
];

export interface LensFlareConfig {
  /** Bright-pixel detection threshold (luminance). Default 0.9. */
  brightThreshold: number;
  /** Ghost-flare definitions. */
  ghosts: LensFlareGhost[];
  /** Multiplier on the entire ghost composite. Default 0.4. */
  ghostIntensity: number;
  /** Enable starburst pattern. Default false. */
  starburstEnabled: boolean;
  /** Starburst spoke count (mirrors the iris-blade count). Default 8. */
  starburstSpokes: number;
  /** Starburst spoke length (UV units). Default 0.05. */
  starburstLength: number;
  /** Starburst intensity multiplier. Default 0.3. */
  starburstIntensity: number;
  /** Enable self-contained horizontal anamorphic streak. Default false. */
  streakEnabled: boolean;
  /** Streak length in pixels. Default 64. */
  streakLength: number;
  /** Streak color tint (typically blue for cinematic look). */
  streakColor: { r: number; g: number; b: number };
  /** Streak intensity. Default 0.5. */
  streakIntensity: number;
}

export function createLensFlareConfig(
  overrides: Partial<LensFlareConfig> = {},
): LensFlareConfig {
  return {
    brightThreshold: overrides.brightThreshold ?? 0.9,
    ghosts: overrides.ghosts ?? DEFAULT_GHOSTS.map((g) => ({ ...g, color: { ...g.color } })),
    ghostIntensity: overrides.ghostIntensity ?? 0.4,
    starburstEnabled: overrides.starburstEnabled ?? false,
    starburstSpokes: overrides.starburstSpokes ?? 8,
    starburstLength: overrides.starburstLength ?? 0.05,
    starburstIntensity: overrides.starburstIntensity ?? 0.3,
    streakEnabled: overrides.streakEnabled ?? false,
    streakLength: overrides.streakLength ?? 64,
    streakColor: overrides.streakColor ?? { r: 0.4, g: 0.7, b: 1.0 },
    streakIntensity: overrides.streakIntensity ?? 0.5,
  };
}

// ─── CPU reference helpers (tested) ─────────────────────────────────────────

/**
 * Mirror a screen UV through the screen center.
 *   ghostUV = 1 - uv
 * Used as the basis for ghost-flare lookups.
 */
export function mirrorThroughCenter(
  uvX: number, uvY: number,
): { x: number; y: number } {
  return { x: 1 - uvX, y: 1 - uvY };
}

/**
 * Compute the ghost-sample UV for a given source UV and ghost scale.
 *
 *   ghostUV = 0.5 + (0.5 - uv) * scale
 *
 * scale = 1 → exact mirror (point opposite screen center)
 * scale = 0 → screen center
 * scale negative → flips quadrant
 */
export function ghostSampleUV(
  uvX: number, uvY: number, scale: number,
): { x: number; y: number } {
  return {
    x: 0.5 + (0.5 - uvX) * scale,
    y: 0.5 + (0.5 - uvY) * scale,
  };
}

/**
 * Distance-from-center radial falloff (smooth attenuation toward edges).
 *   fall = pow(max(0, 1 - 2 * distFromCenter), 2)
 */
export function radialFalloff(uvX: number, uvY: number): number {
  const dx = uvX - 0.5;
  const dy = uvY - 0.5;
  const d = Math.sqrt(dx * dx + dy * dy);
  return Math.max(0, 1 - 2 * d) ** 2;
}

/**
 * Project a world-space sun direction (FROM origin TO sun) to screen UV
 * via a view-projection matrix. Returns the UV + a `visible` flag (false
 * if the sun is behind the camera).
 *
 * Renamed from `projectSunToScreen` to `projectSunToScreenMat` because
 * `src/render/gi/GodRaysPass.ts` already exports a function with the same
 * name but a different signature (takes a `THREE.Camera`, not a matrix).
 * Both are re-exported from the root render barrel and would collide.
 */
export function projectSunToScreenMat(
  sun: { x: number; y: number; z: number },
  viewProjection: Float32Array | number[],
): { uvX: number; uvY: number; visible: boolean } {
  const m = viewProjection;
  const x = sun.x, y = sun.y, z = sun.z;
  // Treat as a direction (w=0 in column-vector convention).
  const cx = m[0] * x + m[4] * y + m[8] * z;
  const cy = m[1] * x + m[5] * y + m[9] * z;
  const cz = m[2] * x + m[6] * y + m[10] * z;
  const cw = m[3] * x + m[7] * y + m[11] * z;
  if (cw <= 0) return { uvX: 0, uvY: 0, visible: false };
  const ndcX = cx / cw;
  const ndcY = cy / cw;
  return {
    uvX: ndcX * 0.5 + 0.5,
    uvY: ndcY * 0.5 + 0.5,
    visible: cz > -cw && cz < cw, // inside the view frustum
  };
}
